
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import ScrollToTop from "@/components/ScrollToTop";
import StyledBulletPoint from "@/components/StyledBulletPoint";

interface ServicePageLayoutProps {
  heroImage: string;
  heroAlt: string;
  heroTitle: string;
  heroText: string;
  children: React.ReactNode;
}

const ServicePageLayout: React.FC<ServicePageLayoutProps> = ({
  heroImage,
  heroAlt,
  heroTitle,
  heroText,
  children
}) => {
  useEffect(() => {
    // Initialize scroll animation observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero Section */}
      <section className="min-h-[60vh] flex items-center relative overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage} 
            alt={heroAlt} 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 to-background"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6 animate-fade-in">
            {heroTitle}
          </h1>
          <p className="text-xl md:text-2xl font-light text-foreground mb-8 max-w-3xl mx-auto animate-fade-in animate-delay-100">
            {heroText}
          </p>
        </div>
      </section>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        {children}
        
        {/* Return to Home Button */}
        <div className="text-center mt-20 reveal-on-scroll">
          <Link to="/">
            <Button variant="outline" className="flex items-center space-x-2">
              <span>← Retour à l'accueil</span>
            </Button>
          </Link>
        </div>
      </div>
      
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default ServicePageLayout;
